import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Activity, TrendingUp, AlertTriangle, CheckCircle2, ArrowDown, ArrowUp,
  Pencil, Save, X, Loader2, History, Scale,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, Cell,
} from "recharts";
import { apiGet, apiPost, formatLKR, formatPct, formatDateTime } from "@/lib/api";
import { CompanyLink, SymbolLink } from "@/components/common/StockLinks";
import { toast } from "sonner";

const HEALTH_COLOURS = {
  good: "hsl(var(--success))",
  fair: "hsl(var(--warning))",
  poor: "hsl(var(--destructive))",
};

const SIGNAL_BADGE = {
  BUY: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30",
  WATCH: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30",
  HOLD: "bg-muted text-muted-foreground border-muted-foreground/30",
  NO_DATA: "bg-muted/40 text-muted-foreground border-muted-foreground/20",
};

const HealthGauge = ({ score, label, colour }) => {
  const radius = 58;
  const circumference = Math.PI * radius; // half-circle
  const ratio = Math.min(1, Math.max(0, (score || 0) / 100));
  const strokeColour = HEALTH_COLOURS[colour] || "hsl(var(--primary))";
  return (
    <div className="flex flex-col items-center" data-testid="health-gauge">
      <svg width="160" height="100" viewBox="0 0 160 100">
        <path
          d="M 20 90 A 60 60 0 0 1 140 90"
          fill="none"
          stroke="hsl(var(--muted))"
          strokeWidth="14"
          strokeLinecap="round"
        />
        <path
          d="M 20 90 A 60 60 0 0 1 140 90"
          fill="none"
          stroke={strokeColour}
          strokeWidth="14"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - ratio)}
          style={{ transition: "stroke-dashoffset 600ms ease" }}
        />
        <text
          x="80"
          y="78"
          textAnchor="middle"
          className="font-display"
          fontSize="32"
          fontWeight="600"
          fill="hsl(var(--foreground))"
        >
          {score}
        </text>
        <text
          x="80"
          y="95"
          textAnchor="middle"
          fontSize="11"
          fill="hsl(var(--muted-foreground))"
        >
          / 100
        </text>
      </svg>
      <div className="mt-1 text-sm font-display font-semibold" style={{ color: strokeColour }}>
        {label}
      </div>
    </div>
  );
};

const PortfolioHealthTab = ({ portfolioId, suggestion, onChange }) => {
  const [data, setData] = useState(null);
  const [exits, setExits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingTarget, setEditingTarget] = useState(false);
  const [draftTargets, setDraftTargets] = useState({});
  const [savingTarget, setSavingTarget] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [h, e] = await Promise.all([
        apiGet(`/portfolios/${portfolioId}/health`),
        apiGet(`/portfolios/${portfolioId}/exits`),
      ]);
      setData(h);
      setExits(e);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Failed");
    } finally {
      setLoading(false);
    }
  }, [portfolioId]);

  useEffect(() => {
    load();
  }, [load]);

  const startEditingTarget = () => {
    const map = {};
    // Seed with current allocation as a sensible starting point so the
    // user has a non-zero default for every held sector even if no
    // target was previously set.
    (data.allocation_drift || []).forEach((row) => {
      map[row.sector] =
        row.target_pct != null
          ? row.target_pct
          : Math.round((row.current_pct || 0) * 10) / 10;
    });
    setDraftTargets(map);
    setEditingTarget(true);
  };

  const saveTarget = async () => {
    setSavingTarget(true);
    try {
      const rows = Object.entries(draftTargets).map(([sector, target_pct]) => ({
        sector,
        target_pct: Number(target_pct) || 0,
      }));
      await apiPost(`/portfolios/${portfolioId}/health/target-allocation`, rows);
      toast.success("Target allocation saved");
      setEditingTarget(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save target allocation");
    } finally {
      setSavingTarget(false);
    }
  };

  const totalDraftPct = useMemo(
    () => Object.values(draftTargets).reduce((sum, v) => sum + (Number(v) || 0), 0),
    [draftTargets],
  );

  if (loading && !data) {
    return (
      <Card className="border bg-card">
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
          Calculating portfolio health…
        </CardContent>
      </Card>
    );
  }
  if (error) {
    return (
      <Card className="border bg-card">
        <CardContent className="py-6 text-sm text-destructive">{error}</CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const drift = data.allocation_drift || [];
  const reb = data.rebalancing;
  const exitStats = exits?.stats;
  const hasTarget = data.has_explicit_target;
  const targetSource = data.target_source;

  return (
    <div className="space-y-5" data-testid="portfolio-health-tab">
      {/* Phase 4 (Item 15) — Explanation strip so users understand what
          the portfolio health score actually measures. */}
      <Card className="border-dashed border-primary/30 bg-primary/5" data-testid="portfolio-health-explainer">
        <CardContent className="p-4 text-sm leading-relaxed">
          <div className="flex items-start gap-2">
            <Activity className="h-4 w-4 mt-0.5 text-primary shrink-0" />
            <div className="space-y-1.5">
              <div className="font-medium">What is Portfolio Health?</div>
              <p className="text-muted-foreground">
                A 0–100 score that combines (a) the average composite
                score of every stock you own, weighted by position size,
                (b) sector concentration risk, and (c) the count of
                holdings flagged with strong AVOID or WATCH signals.
                Above 70 = healthy. 50–70 = monitor. Below 50 = action
                recommended.
              </p>
              <p className="text-muted-foreground">
                Use the rebalancing card below to bring weak holdings
                back in line with your target allocation.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Health header strip */}
      <Card className="border bg-card">
        <CardContent className="py-5 flex flex-col md:flex-row gap-5 items-center md:items-stretch">
          <HealthGauge
            score={data.health_score}
            label={data.health_label}
            colour={data.health_colour}
          />
          <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-3 w-full">
            <KpiMini label="Holdings" value={data.holdings_count} />
            <KpiMini label="Total value" value={formatLKR(data.total_value, { compact: true, decimals: 1 })} />
            <KpiMini
              label="Strong exit signals"
              value={data.exit_signals?.strong || 0}
              accent={(data.exit_signals?.strong || 0) > 0 ? "destructive" : "muted"}
            />
            <KpiMini
              label="Review signals"
              value={data.exit_signals?.review || 0}
              accent={(data.exit_signals?.review || 0) > 0 ? "warning" : "muted"}
            />
          </div>
        </CardContent>
      </Card>

      {(data.health_score < 50) && (
        <div
          className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm flex items-start gap-2"
          data-testid="health-warning-banner"
        >
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(var(--destructive))]" />
          <div>
            <strong>Portfolio health has declined.</strong>{" "}
            {(data.exit_signals?.strong || 0) + (data.exit_signals?.review || 0)} holdings may need review.
          </div>
        </div>
      )}

      {/* Sector allocation: current vs target */}
      <Card className="border bg-card">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="font-display text-base flex items-center gap-2">
                <Scale className="h-4 w-4 text-[hsl(var(--primary))]" /> Sector allocation
              </CardTitle>
              <CardDescription>
                {hasTarget ? (
                  <>
                    Current vs target allocation. Target source:{" "}
                    <span className="font-medium text-foreground">
                      {targetSource === "explicit" ? "manual" : "AI suggestion"}
                    </span>
                    .
                  </>
                ) : (
                  "No target allocation set. Showing current breakdown only."
                )}
              </CardDescription>
            </div>
            {!editingTarget ? (
              <Button
                size="sm"
                variant="outline"
                onClick={startEditingTarget}
                className="gap-1"
                data-testid="edit-target-allocation-button"
              >
                <Pencil className="h-3.5 w-3.5" /> {hasTarget ? "Edit target" : "Set targets"}
              </Button>
            ) : (
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setEditingTarget(false)}
                  className="gap-1"
                >
                  <X className="h-3.5 w-3.5" /> Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={saveTarget}
                  disabled={savingTarget}
                  className="gap-1"
                  data-testid="save-target-allocation-button"
                >
                  {savingTarget ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                  Save
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Descriptive prompt when no target set + not editing */}
          {!hasTarget && !editingTarget && (
            <div
              className="rounded-md border border-warning/30 bg-warning/5 p-3 text-xs text-muted-foreground space-y-1"
              data-testid="no-target-prompt"
            >
              <div className="font-display font-semibold text-foreground">
                Set sector targets to unlock drift warnings + rebalancing
              </div>
              <p>
                You can either define them manually using the {`"Set targets"`} button above, or let
                Claude suggest them based on this portfolio's goal. Targets are also auto-set
                whenever a portfolio is created via the AI suggestion flow.
              </p>
            </div>
          )}

          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <BarChart data={drift} margin={{ top: 10, right: 10, left: 0, bottom: 30 }}>
                <XAxis dataKey="sector" tick={{ fontSize: 10 }} angle={-25} textAnchor="end" />
                <YAxis tick={{ fontSize: 10 }} unit="%" />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="current_pct" name="Current %" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
                {hasTarget && (
                  <Bar dataKey="target_pct" name="Target %" fill="hsl(var(--chart-3))" radius={[4, 4, 0, 0]} />
                )}
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Drift table / editor */}
          <div className="rounded-md border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/30 border-b text-[11px] uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Sector</th>
                  <th className="px-3 py-2 text-right">Current %</th>
                  {(hasTarget || editingTarget) && <th className="px-3 py-2 text-right">Target %</th>}
                  {hasTarget && <th className="px-3 py-2 text-right">Drift</th>}
                </tr>
              </thead>
              <tbody>
                {drift.map((row) => {
                  const drifted = row.drift_pct != null && Math.abs(row.drift_pct) > 10;
                  return (
                    <tr key={row.sector} className="border-b last:border-0">
                      <td className="px-3 py-2.5 font-medium">{row.sector}</td>
                      <td className="px-3 py-2.5 text-right tabular">
                        {row.current_pct.toFixed(1)}%
                      </td>
                      {(hasTarget || editingTarget) && (
                        <td className="px-3 py-2.5 text-right tabular">
                          {editingTarget ? (
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              step="0.5"
                              value={draftTargets[row.sector] ?? 0}
                              onChange={(e) =>
                                setDraftTargets((d) => ({
                                  ...d,
                                  [row.sector]: e.target.value,
                                }))
                              }
                              className="h-8 w-20 text-right tabular ml-auto"
                              data-testid={`target-input-${row.sector}`}
                            />
                          ) : row.target_pct != null ? (
                            `${row.target_pct.toFixed(1)}%`
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                      )}
                      {hasTarget && (
                        <td
                          className={`px-3 py-2.5 text-right tabular ${
                            drifted
                              ? row.drift_pct > 0
                                ? "text-[hsl(var(--warning))]"
                                : "text-[hsl(var(--destructive))]"
                              : "text-muted-foreground"
                          }`}
                        >
                          {row.drift_pct != null
                            ? `${row.drift_pct > 0 ? "+" : ""}${row.drift_pct.toFixed(1)}%`
                            : "—"}
                          {drifted && (
                            <Badge
                              variant="outline"
                              className="ml-2 text-[9px] uppercase border-warning/40 text-warning"
                            >
                              drift
                            </Badge>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
              {editingTarget && (
                <tfoot>
                  <tr className="border-t bg-muted/20">
                    <td className="px-3 py-2 text-[11px] uppercase tracking-wide text-muted-foreground">
                      Total
                    </td>
                    <td className="px-3 py-2 text-right tabular text-muted-foreground" colSpan={1}>
                      —
                    </td>
                    <td
                      className={`px-3 py-2 text-right tabular ${
                        Math.abs(totalDraftPct - 100) > 1
                          ? "text-[hsl(var(--warning))]"
                          : "text-foreground"
                      }`}
                      colSpan={hasTarget ? 1 : 2}
                    >
                      {totalDraftPct.toFixed(1)}%
                      <span className="ml-2 text-[11px] text-muted-foreground">
                        (should sum to ~100%)
                      </span>
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Rebalancing suggestion */}
      {reb && reb.actions?.length > 0 && (
        <Card className="border bg-card border-warning/30" data-testid="rebalancing-card">
          <CardHeader className="pb-3">
            <CardTitle className="font-display text-base flex items-center gap-2">
              📊 Rebalancing Suggested
            </CardTitle>
            <CardDescription>{reb.summary}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {reb.actions.map((a, i) => (
              <div
                key={i}
                className={`rounded-md border p-3 ${
                  a.type === "reduce"
                    ? "border-warning/30 bg-warning/5"
                    : "border-[hsl(var(--success))]/30 bg-[hsl(var(--success))]/5"
                }`}
                data-testid={`rebalance-action-${a.type}-${i}`}
              >
                <div className="flex items-start gap-2">
                  {a.type === "reduce" ? (
                    <ArrowDown className="h-4 w-4 mt-0.5 text-[hsl(var(--warning))] shrink-0" />
                  ) : (
                    <ArrowUp className="h-4 w-4 mt-0.5 text-[hsl(var(--success))] shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-display font-semibold">
                      {a.type === "reduce" ? "REDUCE" : "ADD"} — {a.sector}
                    </div>
                    <div className="text-sm mt-0.5">
                      {a.type === "reduce" ? "Sell" : "Buy"} <strong>{a.shares}</strong> shares of{" "}
                      <CompanyLink
                        companyCode={a.company_code}
                        name={a.company_name}
                        fromLabel="Health"
                        className="text-primary"
                      />{" "}
                      <SymbolLink
                        companyCode={a.company_code}
                        symbol={a.cse_symbol}
                        fromLabel="Health"
                        className="text-muted-foreground"
                      />
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {a.type === "reduce" ? "Realise" : "Cost"}:{" "}
                      <span className="tabular">{formatLKR(a.value_lkr)}</span>
                      {a.type === "add" && a.estimated_annual_dividend_lkr != null && (
                        <span className="ml-2">
                          · Est. annual dividend:{" "}
                          <span className="tabular">{formatLKR(a.estimated_annual_dividend_lkr)}</span>
                        </span>
                      )}
                      {a.type === "add" && a.score != null && (
                        <span className="ml-2">· Score {a.score}/100</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Holdings sorted by signal strength with exit-signal banners */}
      <Card className="border bg-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-display text-base flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-[hsl(var(--primary))]" />
            Holdings by signal strength
          </CardTitle>
          <CardDescription>
            Sorted strongest first (BUY → WATCH → HOLD). Any active exit signals are flagged.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {(data.holdings_sorted || []).map((h) => (
            <div
              key={h.holding_id}
              className="rounded-md border bg-background p-2.5 flex flex-wrap items-center gap-3"
              data-testid={`health-holding-row-${h.holding_id}`}
            >
              <div className="min-w-0 flex-1">
                <CompanyLink
                  companyCode={h.company_code}
                  name={h.company_name}
                  fromLabel="Portfolio"
                  className="text-sm font-medium"
                />
                <div className="text-xs text-muted-foreground flex items-center gap-2">
                  <SymbolLink
                    companyCode={h.company_code}
                    symbol={h.cse_symbol}
                    fromLabel="Portfolio"
                    className="text-muted-foreground"
                  />
                  <span>{h.sector}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge
                  variant="outline"
                  className={`text-[10px] ${SIGNAL_BADGE[h.composite_signal] || SIGNAL_BADGE.NO_DATA}`}
                >
                  {h.composite_signal || "NO_DATA"} · {h.composite_score ?? "—"}
                </Badge>
                <span className="text-xs text-muted-foreground tabular">
                  {formatLKR(h.current_value, { compact: true, decimals: 1 })}
                </span>
                {h.exit_signal_status === "strong" && (
                  <Badge
                    variant="outline"
                    className="text-[10px] bg-[hsl(var(--destructive))]/15 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30"
                    title={h.exit_signal_reason}
                  >
                    🔴 Exit
                  </Badge>
                )}
                {h.exit_signal_status === "review" && (
                  <Badge
                    variant="outline"
                    className="text-[10px] bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30"
                    title={h.exit_signal_reason}
                  >
                    🟡 Review
                  </Badge>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Exit history */}
      {exitStats && exitStats.total_exits > 0 && (
        <Card className="border bg-card">
          <CardHeader className="pb-3">
            <CardTitle className="font-display text-base flex items-center gap-2">
              <History className="h-4 w-4 text-[hsl(var(--primary))]" />
              📤 Exit History
            </CardTitle>
            <CardDescription>
              You have closed {exitStats.total_exits} position{exitStats.total_exits === 1 ? "" : "s"}:{" "}
              {exitStats.profitable} profitable · {exitStats.losses} loss{exitStats.losses === 1 ? "" : "es"}{" "}
              · {exitStats.rebalancing} rebalancing.{" "}
              <span className="font-medium text-foreground">
                Total realised P/L: {formatLKR(exitStats.total_realised_pl_lkr)}
              </span>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-1.5">
            {(exits.exits || []).slice(0, 10).map((e) => {
              const positive = (e.realised_pl_lkr || 0) >= 0;
              return (
                <div
                  key={e.holding_id}
                  className="rounded-md border p-2 flex flex-wrap items-center gap-3 text-xs"
                  data-testid={`exit-row-${e.holding_id}`}
                >
                  <SymbolLink
                    companyCode={e.company_code}
                    symbol={e.cse_symbol}
                    fromLabel="Portfolio"
                  />
                  <span className="text-muted-foreground">{e.shares} sh</span>
                  <span className="text-muted-foreground">
                    @ {formatLKR(e.exit_price)} (was {formatLKR(e.purchase_price)})
                  </span>
                  <span
                    className={`tabular font-medium ${
                      positive ? "text-[hsl(var(--success))]" : "text-[hsl(var(--destructive))]"
                    }`}
                  >
                    {positive ? "+" : ""}
                    {formatLKR(e.realised_pl_lkr)}
                  </span>
                  <Badge variant="secondary" className="text-[9px] uppercase">
                    {(e.exit_reason || "").replace(/_/g, " ")}
                  </Badge>
                  <span className="text-muted-foreground ml-auto">
                    {formatDateTime(e.exited_at)}
                  </span>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const KpiMini = ({ label, value, accent = "muted" }) => {
  const tones = {
    muted: "text-foreground",
    destructive: "text-[hsl(var(--destructive))]",
    warning: "text-[hsl(var(--warning))]",
  };
  return (
    <div className="rounded-md border bg-background p-3">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`mt-1 text-lg font-display font-semibold tabular ${tones[accent]}`}>{value}</div>
    </div>
  );
};

export default PortfolioHealthTab;
